import axios from "./axios_interceptor";
import { get_jwt_token, loginRedirect } from "./helper_funtions";

const transformConfigToFrontend = (config) => {
  if (!config) return config;
  
  const services = config.services || config.service || [];
  const regions = config.regions || (config.region_name ? [config.region_name] : []) || config.region || [];
  
  return {
    ...config,
    platform: (config.provider_type || config.platform || '').toLowerCase(),
    service: services,
    region: regions,
    configuration_name: config.name || config.configuration_name,
    type: config.provider_type || config.type || config.platform || '-',
    what_to_scan: services,
    aws_region: regions.map(r => typeof r === 'string' ? { label: r, value: r } : r),
    name: config.name,
    services: services,
    regions: regions,
    provider_type: config.provider_type,
    permission_errors: config.permission_errors || [],
  };
};

export const getCloudConfigurations = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    let url = `/cloud-discovery/configs/`;
    const params = new URLSearchParams();
    if (data.page !== undefined) {
      params.append("page", data.page);
    }
    if (data.page_size !== undefined) {
      params.append("limit", data.page_size);
    }
    if (data.search) {
      params.append("search", data.search);
    }
    if (data.filter_by) {
      params.append("filter_by", data.filter_by);
    }
    if (data.provider_type) {
      params.append("provider_type", data.provider_type);
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    const response = await axios.get(url, config);
    const backendData = response.data;
    const configs = (backendData.configs || []).map(transformConfigToFrontend);
    
    return {
      data: {
        results: configs,
        count: configs.length,
        pagination: backendData.pagination || {},
      },
      error: null,
    };
  } catch (e) {
    return { data: null, error: e.response?.data?.errors || e.response?.data };
  }
};

export const getCloudConfigurationById = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const configId = typeof data === 'string' || typeof data === 'number' ? data : (data.config_id || data.id);
    const url = `/cloud-discovery/configs/?config_id=${configId}`;
    const response = await axios.get(url, config);
    
    const backendConfig = response.data.config || response.data;
    const transformedConfig = transformConfigToFrontend(backendConfig);
    
    return { 
      data: transformedConfig,
      error: null 
    };
  } catch (e) {
    return { data: null, error: e.response?.data?.errors || e.response?.data };
  }
};

const transformPayloadToBackend = (data) => {
  const payload = { ...data };
  
  if (payload.configuration_name) {
    payload.name = payload.configuration_name;
    delete payload.configuration_name;
  }
  
  if (payload.platform) {
    payload.provider_type = payload.platform.toUpperCase();
    delete payload.platform;
  }
  
  if (payload.what_to_scan) {
    payload.services = payload.what_to_scan;
    delete payload.what_to_scan;
  }
  
  if (payload.aws_region) {
    if (typeof payload.aws_region === 'string') {
      payload.regions = [payload.aws_region];
    } else if (typeof payload.aws_region === 'object' && payload.aws_region.value) {
      payload.regions = [payload.aws_region.value];
    } else if (Array.isArray(payload.aws_region)) {
      payload.regions = payload.aws_region.map(r => typeof r === 'object' && r.value ? r.value : r);
    } else {
      payload.regions = [payload.aws_region];
    }
    delete payload.aws_region;
  }
  
  delete payload.auto_onboard;
  delete payload.id;
  
  return payload;
};

export const addCloudConfiguration = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const url = `/cloud-discovery/configs/`;
    const backendPayload = transformPayloadToBackend(data);
    const response = await axios.post(url, backendPayload, config);
    
    const backendConfig = response.data.config || response.data;
    const transformedConfig = backendConfig ? transformConfigToFrontend(backendConfig) : response.data;
    
    return { 
      data: {
        ...response.data,
        config: transformedConfig,
      },
      error: null 
    };
  } catch (e) {
    return { data: null, error: e.response?.data || null };
  }
};

export const updateCloudConfiguration = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const { config_id, id, ...updateData } = data;
    const configId = config_id || id;
    const url = `/cloud-discovery/configs/?config_id=${configId}`;
    
    const backendPayload = transformPayloadToBackend(updateData);
    
    const response = await axios.put(url, backendPayload, config);
    
    const backendConfig = response.data.config || response.data;
    const transformedConfig = backendConfig ? transformConfigToFrontend(backendConfig) : response.data;
    
    return { 
      data: {
        ...response.data,
        config: transformedConfig,
      },
      error: null 
    };
  } catch (e) {
    return { data: null, error: e.response?.data || null };
  }
};

export const deleteCloudConfigurations = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: data,
    };
    const url = `/cloud-discovery/configs/`;
    const response = await axios.delete(url, config);
    return { data: response.data, error: null };
  } catch (e) {
    return { data: null, error: e.response?.data?.errors || e.response?.data };
  }
};

export const scanCloudConfiguration = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const url = `/cloud-discovery/start-scan/`;
    const payload = {
      config_id: data.id || data.config_id,
    };
    const response = await axios.post(url, payload, config);
    return { data: response.data, error: null };
  } catch (e) {
    return { data: null, error: e.response?.data?.errors || e.response?.data };
  }
};

export const getCloudScanHistory = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    let url = `/cloud-discovery/scan-history/`;
    const params = new URLSearchParams();
    if (data.page !== undefined) {
      const backendPage = Math.max(0, data.page - 1);
      params.append("page", backendPage);
    }
    if (data.limit !== undefined) {
      params.append("limit", data.limit);
    }
    if (data.page_size !== undefined) {
      params.append("limit", data.page_size);
    }
    if (data.search) {
      params.append("search", data.search);
    }
    if (data.scan_id) {
      params.append("scan_id", data.scan_id);
    }
    if (data.config_id || data.id) {
      params.append("config_id", data.config_id || data.id);
    }
    if (data.filter_by) {
      params.append("filter_by", data.filter_by);
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    const response = await axios.get(url, config);
    
    const backendData = response.data;
    
    if (data.scan_id && backendData.scan) {
      return {
        data: {
          scan: backendData.scan,
        },
        error: null
      };
    }
    
    const scans = backendData.scans || [];
    
    return { 
      data: {
        results: scans,
        scans: scans,
        count: backendData.total || scans.length,
        total: backendData.total || scans.length,
      },
      error: null 
    };
  } catch (e) {
    return { data: null, error: e.response?.data?.errors || e.response?.data };
  }
};

export const getScanResults = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    let url = `/cloud-discovery/scan-results/`;
    const params = new URLSearchParams();
    
    const scanId = data.scan_id || data.historyId;
    if (scanId) {
      params.append("scan_id", scanId);
    }
    
    const limit = data.limit || data.pageSize;
    if (data.page !== undefined) {
      const backendPage = Math.max(0, data.page - 1);
      params.append("page", backendPage);
    }
    if (limit !== undefined) {
      params.append("limit", limit);
    }
    if (data.search) {
      params.append("search", data.search);
    }
    if (data.resource_type) {
      params.append("resource_type", data.resource_type);
    }
    if (data.filter_by) {
      params.append("filter_by", data.filter_by);
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    const response = await axios.get(url, config);
    
    const backendData = response.data;
    const resources = backendData.resources || [];
    
    return { 
      data: {
        results: resources,
        count: backendData.total || resources.length,
        total: backendData.total || resources.length,
        resources: resources,
      },
      error: null 
    };
  } catch (e) {
    return { data: null, error: e.response?.data?.errors || e.response?.data };
  }
};

export const pollCloudConfigurationStatus = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const configId = typeof data === 'string' || typeof data === 'number' ? data : (data.config_id || data.id);
    const url = `/cloud-discovery/poll-status/`;
    const response = await axios.post(url, { config_id: configId }, config);
    
    const backendConfig = response.data.config || response.data;
    const transformedConfig = backendConfig ? transformConfigToFrontend(backendConfig) : response.data;
    
    return { 
      data: {
        ...response.data,
        config: transformedConfig,
      },
      error: null 
    };
  } catch (e) {
    return { data: null, error: e.response?.data?.errors || e.response?.data };
  }
};

export const onboardAssets = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const url = `/cloud-discovery/onboard-assets/`;
    const response = await axios.post(url, data, config);
    return { data: response.data, error: null };
  } catch (e) {
    return { data: null, error: e.response?.data?.errors || e.response?.data };
  }
};

export const checkIPReachability = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000,
    };
    const url = `/cloud-discovery/check-ip-reachability/`;
    const response = await axios.post(url, { 
      ip_address: data.ip_address,
      port: data.port || 22,
      bypass_cache: true,
      machine_status: data.machine_status
    }, config);
    return { data: response.data, error: null, status: response.status };
  } catch (e) {
    const errorData = {
      message: e.response?.data?.errors || e.response?.data?.message || e.message,
      status: e.response?.status,
      data: e.response?.data
    };
    return { data: null, error: errorData, status: e.response?.status || null };
  }
};

export const checkBatchIPReachability = async (checks, timeout = 3, bypass_cache = false) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 30000,
    };
    const url = `/cloud-discovery/check-ip-reachability/`;
    // checks array can include machine_status for each check
    const response = await axios.post(url, { 
      checks: checks,
      timeout: timeout,
      bypass_cache: bypass_cache
    }, config);
    return { data: response.data, error: null, status: response.status };
  } catch (e) {
    const errorData = {
      message: e.response?.data?.errors || e.response?.data?.message || e.message,
      status: e.response?.status,
      data: e.response?.data
    };
    return { data: null, error: errorData, status: e.response?.status || null };
  }
};

export const refreshResourceStatus = async (data) => {
  try {
    const token = get_jwt_token();
    loginRedirect(token);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      timeout: 15000,
    };
    const url = `/cloud-discovery/refresh-resource-status/`;
    const response = await axios.post(url, {
      resource_id: data.resource_id,
      scan_id: data.scan_id
    }, config);
    return { data: response.data, error: null, status: response.status };
  } catch (e) {
    const errorData = {
      message: e.response?.data?.errors || e.response?.data?.message || e.message,
      status: e.response?.status,
      data: e.response?.data
    };
    return { data: null, error: errorData, status: e.response?.status || null };
  }
};

